import type { JSONContent } from "@tiptap/core";
import { assertBlocks, type Block, type Cell, type CodeLine, type Inline } from "./blocks.ts";
import { SchemaError } from "./errors.ts";
import {
  canonicalInlineWithin,
  type InlineToken,
  isSafeHref,
  type MinimizeBudget,
  minimizeBudget,
  parseInline,
  serializeInlineWithin,
} from "./inline.ts";

export type TiptapNode = JSONContent;
export type TiptapDoc = { type: "doc"; content: TiptapNode[] };

// ── blocks → Tiptap ──────────────────────────────────────────────────────────

function tokenNode(t: InlineToken): TiptapNode {
  switch (t.t) {
    case "text":
      return t.mark ? { type: "text", text: t.text, marks: [{ type: t.mark }] } : { type: "text", text: t.text };
    case "link":
      return { type: "text", text: t.text, marks: [{ type: "link", attrs: { href: t.href } }] };
    case "doc":
      return { type: "docLink", attrs: { code: t.code, text: t.text } };
    case "placeholder":
      return { type: "placeholder", attrs: { label: t.label } };
  }
}

const inlineNodes = (s: Inline): TiptapNode[] => parseInline(s).map(tokenNode);

const withContent = (node: TiptapNode, content: TiptapNode[]): TiptapNode =>
  content.length ? { ...node, content } : node;

const textContent = (s: string): TiptapNode[] => (s ? [{ type: "text", text: s }] : []);

/**
 * The inline content of one paragraph made from several blocks (a header cell, a callout): each
 * paragraph's inline content and the plain text of anything else, joined with a single space.
 * Shared by tiptapToBlocks (header cells) and normalizeTiptap.
 */
export function mergeToInline(blocks: TiptapNode[], plain: (node: TiptapNode, index: number) => string): TiptapNode[] {
  return blocks
    .map((b, i) => (b.type === "paragraph" ? (b.content ?? []) : textContent(plain(b, i))))
    .filter((c) => c.length > 0)
    .flatMap((c, i) => (i === 0 ? c : [{ type: "text", text: " " }, ...c]));
}
const paragraph = (s: Inline): TiptapNode => withContent({ type: "paragraph" }, inlineNodes(s));
const codeLine = (s: string): TiptapNode => withContent({ type: "klCodeLine" }, textContent(s));
const cellContent = (cell: Cell): TiptapNode[] =>
  typeof cell === "string"
    ? [paragraph(cell)]
    : cell.map((x) => (typeof x === "string" ? paragraph(x) : codeLine(x.text)));

function blockToNode(b: Block): TiptapNode {
  switch (b.type) {
    case "h1":
    case "h2":
      return withContent(
        { type: "heading", attrs: { level: b.type === "h1" ? 1 : 2, number: b.number !== false } },
        textContent(b.title),
      );
    case "p":
      return paragraph(b.text);
    case "kv":
      return {
        type: "klKeyValue",
        content: b.pairs.map(([key, value]) => withContent({ type: "klKvPair", attrs: { key } }, inlineNodes(value))),
      };
    case "bullets":
    case "steps":
      return {
        type: b.type === "bullets" ? "bulletList" : "orderedList",
        content: b.items.map((item) => ({ type: "listItem", content: [paragraph(item)] })),
      };
    case "table":
      return {
        type: "klTable",
        attrs: { widths: b.widths ? [...b.widths] : null, firstColBold: b.firstColBold ?? true },
        content: [
          {
            type: "klTableHeaderRow",
            content: b.columns.map((c) => ({ type: "klTableCell", content: [paragraph(c)] })),
          },
          ...b.rows.map((row) => ({
            type: "klTableRow",
            content: row.map((cell) => ({ type: "klTableCell", content: cellContent(cell) })),
          })),
        ],
      };
    case "groupTable":
      return {
        type: "klGroupList",
        content: b.groups.map((g) => ({
          type: "klGroup",
          attrs: { title: g.title, meta: g.meta ? g.meta : null },
          content: g.items.map((item) => ({ type: "klGroupItem", content: cellContent(item) })),
        })),
      };
    case "code":
      return withContent({ type: "codeBlock" }, textContent(b.text));
    case "note":
    case "warning":
      return {
        type: "klCallout",
        attrs: { kind: b.type, title: b.title ? b.title : null },
        content: [paragraph(b.text)],
      };
    case "pageBreak":
      return { type: "pageBreak" };
    case "image":
      return {
        type: "klImage",
        attrs: { src: b.src, caption: b.caption ? b.caption : null, alt: b.alt ? b.alt : null },
      };
  }
}

/** Convert blocks to a Tiptap document. An empty block list gives one empty paragraph. */
export function blocksToTiptap(blocks: Block[]): TiptapDoc {
  const content = blocks.map(blockToNode);
  return { type: "doc", content: content.length ? content : [{ type: "paragraph" }] };
}

// ── Tiptap → blocks ──────────────────────────────────────────────────────────

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === "object" && x !== null && !Array.isArray(x);

function children(node: TiptapNode, path: string): TiptapNode[] {
  const c = node.content ?? [];
  if (!Array.isArray(c)) throw new SchemaError("content must be an array", path);
  return c;
}

function only(node: TiptapNode, type: string, path: string): TiptapNode[] {
  const kids = children(node, path);
  kids.forEach((k, i) => {
    if (k.type !== type) throw new SchemaError(`expected ${type}, got ${String(k.type)}`, `${path}.content[${i}]`);
  });
  return kids;
}

function stringAttr(node: TiptapNode, name: string, path: string): string {
  const v: unknown = node.attrs?.[name];
  if (typeof v !== "string") throw new SchemaError(`attribute ${name} must be a string`, `${path}.attrs.${name}`);
  return v;
}

function optionalStringAttr(node: TiptapNode, name: string, path: string): string | undefined {
  const v: unknown = node.attrs?.[name];
  if (v === null || v === undefined || v === "") return undefined;
  if (typeof v !== "string")
    throw new SchemaError(`attribute ${name} must be a string or null`, `${path}.attrs.${name}`);
  return v;
}

/** Plain text of a node whose content is text*, without marks. */
function plainText(node: TiptapNode, path: string): string {
  return children(node, path)
    .map((k, i) => {
      const p = `${path}.content[${i}]`;
      if (k.type !== "text" || typeof k.text !== "string")
        throw new SchemaError(`expected text, got ${String(k.type)}`, p);
      if (k.marks?.length) throw new SchemaError("marks are not allowed here", p);
      return k.text;
    })
    .join("");
}

function inlineText(nodes: TiptapNode[], path: string, budget: MinimizeBudget): Inline {
  const tokens = nodes.map((k, i): InlineToken => {
    const p = `${path}.content[${i}]`;
    switch (k.type) {
      case "text": {
        if (typeof k.text !== "string" || !k.text) throw new SchemaError("text node without text", p);
        const marks = k.marks ?? [];
        if (marks.length > 1) throw new SchemaError("a text run carries at most one mark", p);
        const mark = marks[0];
        if (!mark) return { t: "text", text: k.text };
        if (mark.type === "bold" || mark.type === "italic" || mark.type === "code") {
          return { t: "text", text: k.text, mark: mark.type };
        }
        if (mark.type === "link") {
          const href: unknown = mark.attrs?.href;
          if (typeof href !== "string" || !isSafeHref(href)) throw new SchemaError("unsupported link target", p);
          return { t: "link", text: k.text, href };
        }
        throw new SchemaError(`unsupported mark "${String(mark.type)}"`, p);
      }
      case "docLink":
        return { t: "doc", text: stringAttr(k, "text", p), code: stringAttr(k, "code", p) };
      case "placeholder":
        return { t: "placeholder", label: stringAttr(k, "label", p) };
      default:
        throw new SchemaError(`unsupported inline node "${String(k.type)}"`, p);
    }
  });
  try {
    return serializeInlineWithin(tokens, budget);
  } catch (e) {
    throw e instanceof SchemaError ? new SchemaError(e.message, path) : e;
  }
}

function singleParagraph(node: TiptapNode, path: string, budget: MinimizeBudget): Inline {
  const kids = only(node, "paragraph", path);
  if (kids.length !== 1 || !kids[0]) throw new SchemaError("expected exactly one paragraph", path);
  return inlineText(children(kids[0], `${path}.content[0]`), `${path}.content[0]`, budget);
}

/** A header cell holds (paragraph | klCodeLine)+ (C3); its column title is their content merged into one paragraph. */
function headerCell(node: TiptapNode, path: string, budget: MinimizeBudget): Inline {
  const kids = children(node, path);
  if (!kids.length) throw new SchemaError("a cell needs a paragraph or code line", path);
  kids.forEach((k, i) => {
    const p = `${path}.content[${i}]`;
    if (k.type === "paragraph") children(k, p);
    else if (k.type !== "klCodeLine")
      throw new SchemaError(`expected paragraph or klCodeLine, got ${String(k.type)}`, p);
  });
  const [first] = kids;
  if (kids.length === 1 && first?.type === "paragraph")
    return inlineText(children(first, `${path}.content[0]`), `${path}.content[0]`, budget);
  return inlineText(
    mergeToInline(kids, (k, i) => plainText(k, `${path}.content[${i}]`)),
    path,
    budget,
  );
}

function cellFrom(node: TiptapNode, path: string, budget: MinimizeBudget): Cell {
  const kids = children(node, path);
  if (!kids.length) throw new SchemaError("a cell needs a paragraph or code line", path);
  const items = kids.map((k, i): Inline | CodeLine => {
    const p = `${path}.content[${i}]`;
    if (k.type === "paragraph") return inlineText(children(k, p), p, budget);
    if (k.type === "klCodeLine") return { type: "code", text: plainText(k, p) };
    throw new SchemaError(`expected paragraph or klCodeLine, got ${String(k.type)}`, p);
  });
  const [first] = items;
  return items.length === 1 && typeof first === "string" ? first : items;
}

const allOnes = (w: number[]) => w.every((x) => x === 1);

function nodeToBlock(node: TiptapNode, path: string, budget: MinimizeBudget): Block | null {
  if (!isObject(node)) throw new SchemaError("expected a node", path);
  switch (node.type) {
    case "heading": {
      const level: unknown = node.attrs?.level;
      if (level !== 1 && level !== 2) throw new SchemaError("heading level must be 1 or 2", `${path}.attrs.level`);
      const title = plainText(node, path);
      const type = level === 1 ? "h1" : "h2";
      return node.attrs?.number === false ? { type, title, number: false } : { type, title };
    }
    case "paragraph": {
      const text = inlineText(children(node, path), path, budget);
      return text ? { type: "p", text } : null;
    }
    case "klKeyValue":
      return {
        type: "kv",
        pairs: only(node, "klKvPair", path).map((pair, i): [string, Inline] => {
          const p = `${path}.content[${i}]`;
          return [stringAttr(pair, "key", p), inlineText(children(pair, p), p, budget)];
        }),
      };
    case "bulletList":
    case "orderedList":
      return {
        type: node.type === "bulletList" ? "bullets" : "steps",
        items: only(node, "listItem", path).map((li, i) => singleParagraph(li, `${path}.content[${i}]`, budget)),
      };
    case "klTable": {
      const [header, ...rows] = children(node, path);
      if (header?.type !== "klTableHeaderRow") throw new SchemaError("a table starts with klTableHeaderRow", path);
      const columns = only(header, "klTableCell", `${path}.content[0]`).map((cell, i) =>
        headerCell(cell, `${path}.content[0].content[${i}]`, budget),
      );
      const body = rows.map((row, r) => {
        const p = `${path}.content[${r + 1}]`;
        if (row.type !== "klTableRow") throw new SchemaError(`expected klTableRow, got ${String(row.type)}`, p);
        return only(row, "klTableCell", p).map((cell, c) => cellFrom(cell, `${p}.content[${c}]`, budget));
      });
      const widths: unknown = node.attrs?.widths ?? null;
      if (widths !== null && !(Array.isArray(widths) && widths.every((x) => typeof x === "number"))) {
        throw new SchemaError("widths must be an array of numbers or null", `${path}.attrs.widths`);
      }
      const firstColBold: unknown = node.attrs?.firstColBold ?? true;
      if (typeof firstColBold !== "boolean")
        throw new SchemaError("firstColBold must be a boolean", `${path}.attrs.firstColBold`);
      return {
        type: "table",
        columns,
        ...(widths && !allOnes(widths) ? { widths: [...widths] } : {}),
        rows: body,
        firstColBold,
      };
    }
    case "klGroupList":
      return {
        type: "groupTable",
        groups: only(node, "klGroup", path).map((g, i) => {
          const p = `${path}.content[${i}]`;
          const meta = optionalStringAttr(g, "meta", p);
          return {
            title: stringAttr(g, "title", p),
            ...(meta ? { meta } : {}),
            items: only(g, "klGroupItem", p).map((item, j) => cellFrom(item, `${p}.content[${j}]`, budget)),
          };
        }),
      };
    case "codeBlock":
      return { type: "code", text: plainText(node, path) };
    case "klCallout": {
      const kind: unknown = node.attrs?.kind;
      if (kind !== "note" && kind !== "warning")
        throw new SchemaError('callout kind must be "note" or "warning"', `${path}.attrs.kind`);
      const title = optionalStringAttr(node, "title", path);
      return { type: kind, text: singleParagraph(node, path, budget), ...(title ? { title } : {}) };
    }
    case "pageBreak":
      return { type: "pageBreak" };
    case "klImage": {
      const caption = optionalStringAttr(node, "caption", path);
      const alt = optionalStringAttr(node, "alt", path);
      return {
        type: "image",
        src: stringAttr(node, "src", path),
        ...(caption ? { caption } : {}),
        ...(alt ? { alt } : {}),
      };
    }
    default:
      throw new SchemaError(`unsupported node "${String(node.type)}"`, path);
  }
}

/** Convert a Tiptap document to blocks. Empty paragraphs are dropped. Throws SchemaError outside contract C3. */
export function tiptapToBlocks(doc: TiptapDoc): Block[] {
  if (!isObject(doc) || doc.type !== "doc" || !Array.isArray(doc.content)) {
    throw new SchemaError("expected a doc node with content", "doc");
  }
  const budget = minimizeBudget();
  const blocks = doc.content.flatMap((node, i) => {
    const b = nodeToBlock(node, `doc.content[${i}]`, budget);
    return b ? [b] : [];
  });
  return assertBlocks(blocks);
}

// ── canonical form ───────────────────────────────────────────────────────────

function canonicalCell(cell: Cell, budget: MinimizeBudget): Cell {
  if (typeof cell === "string") return canonicalInlineWithin(cell, budget);
  const items = cell.map((x): Inline | CodeLine =>
    typeof x === "string" ? canonicalInlineWithin(x, budget) : { type: "code", text: x.text },
  );
  const [first] = items;
  return items.length === 1 && typeof first === "string" ? first : items;
}

function canonicalBlock(b: Block, budget: MinimizeBudget): Block | null {
  const inline = (s: Inline) => canonicalInlineWithin(s, budget);
  switch (b.type) {
    case "h1":
    case "h2":
      return b.number === false ? { type: b.type, title: b.title, number: false } : { type: b.type, title: b.title };
    case "p": {
      const text = inline(b.text);
      return text ? { type: "p", text } : null;
    }
    case "kv":
      return { type: "kv", pairs: b.pairs.map(([k, v]): [string, Inline] => [k, inline(v)]) };
    case "bullets":
    case "steps":
      return { type: b.type, items: b.items.map(inline) };
    case "table":
      return {
        type: "table",
        columns: b.columns.map(inline),
        ...(b.widths && !allOnes(b.widths) ? { widths: [...b.widths] } : {}),
        rows: b.rows.map((row) => row.map((cell) => canonicalCell(cell, budget))),
        firstColBold: b.firstColBold ?? true,
      };
    case "groupTable":
      return {
        type: "groupTable",
        groups: b.groups.map((g) => ({
          title: g.title,
          ...(g.meta ? { meta: g.meta } : {}),
          items: g.items.map((cell) => canonicalCell(cell, budget)),
        })),
      };
    case "code":
      return { type: "code", text: b.text };
    case "note":
    case "warning":
      return { type: b.type, text: inline(b.text), ...(b.title ? { title: b.title } : {}) };
    case "pageBreak":
      return { type: "pageBreak" };
    case "image":
      return {
        type: "image",
        src: b.src,
        ...(b.caption ? { caption: b.caption } : {}),
        ...(b.alt ? { alt: b.alt } : {}),
      };
  }
}

/**
 * Canonical form of a block list: what tiptapToBlocks(blocksToTiptap(b)) returns.
 * Sets firstColBold, drops all-ones widths, empty paragraphs and empty optional strings,
 * and canonicalises inline markup.
 */
export function canonical(blocks: Block[]): Block[] {
  const budget = minimizeBudget();
  return blocks.flatMap((b) => {
    const c = canonicalBlock(b, budget);
    return c ? [c] : [];
  });
}
